/**
 * Settings for the calendar and for leave requests: what the app shows, who may add and
 * approve, and the list of things an employee can ask for.
 *
 * Saved through the same PATCH as every other company setting. The request types are one
 * setting holding the whole list, so adding, renaming and removing are all "send the
 * list as it now is".
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Switch } from '@/components/Switch';
import { Card, Field } from '@/components/ui';
import { usePermissions } from '@/features/access/usePermissions';
import { useI18n } from '@/i18n';
import { api, ApiError, type LeaveType } from '@/lib/api';

/**
 * A key for a request type, made once from its Italian name and never changed.
 *
 * Requests store the key, not the name, so renaming a type later must not break the ones
 * already filed. Lowercase letters, digits and underscores, at most 30 (what the API
 * accepts), and unique against the keys already taken.
 */
export function leaveTypeKey(name: string, taken: ReadonlySet<string>): string {
  const slug = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 24);
  const base = slug || 'type';
  let key = base;
  for (let n = 2; taken.has(key); n += 1) key = `${base}_${n}`;
  return key;
}

/** A type in the editor: `key` is null until it is saved for the first time. */
interface DraftType {
  key: string | null;
  it: string;
  en: string;
  hours: boolean;
}

const SWITCHES = [
  {
    key: 'calendar_in_app',
    prop: 'calendarInApp',
    labelKey: 'settings.calLeave.calendarInApp',
    hintKey: 'settings.calLeave.calendarInAppHint',
  },
  {
    key: 'calendar_employees_can_add',
    prop: 'calendarEmployeesCanAdd',
    labelKey: 'settings.calLeave.employeesCanAdd',
    hintKey: 'settings.calLeave.employeesCanAddHint',
  },
  {
    key: 'leave_enabled',
    prop: 'leaveEnabled',
    labelKey: 'settings.calLeave.leaveEnabled',
    hintKey: 'settings.calLeave.leaveEnabledHint',
  },
  {
    key: 'leave_managers_can_approve',
    prop: 'leaveManagersCanApprove',
    labelKey: 'settings.calLeave.managersCanApprove',
    hintKey: 'settings.calLeave.managersCanApproveHint',
  },
] as const;

export function CalendarLeaveSettings() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: api.settings });
  const readOnly = !usePermissions().can('settings', 'write');

  const [switches, setSwitches] = useState<Record<string, boolean>>({});
  const [types, setTypes] = useState<DraftType[]>([]);
  const [saved, setSaved] = useState(false);
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    const s = settings.data?.settings;
    if (!s) return;
    setSwitches(Object.fromEntries(SWITCHES.map((x) => [x.key, s[x.prop]])));
    setTypes((s.leaveTypes ?? []).map((x) => ({ ...x })));
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => {
      const taken = new Set(types.flatMap((x) => (x.key ? [x.key] : [])));
      const list: LeaveType[] = types.map((x) => {
        const it = x.it.trim();
        const key = x.key ?? leaveTypeKey(it, taken);
        taken.add(key);
        return { key, it, en: x.en.trim() || it, hours: x.hours };
      });
      return api.updateSettings({ ...switches, leave_types: list });
    },
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['settings'] });
      await queryClient.invalidateQueries({ queryKey: ['leave-config'] });
      setTimeout(() => setSaved(false), 4000);
    },
  });

  if (!settings.data) return null;

  const patchType = (index: number, change: Partial<DraftType>) => {
    setInvalid(false);
    setTypes(types.map((x, i) => (i === index ? { ...x, ...change } : x)));
  };

  const submit = () => {
    if (types.length === 0 || types.some((x) => x.it.trim() === '')) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    save.mutate();
  };

  return (
    // A view-only permission sees the settings without a save button the API would refuse.
    <fieldset disabled={readOnly}>
      <Card>
        <h2 className="font-bold">{t('settings.calLeave.title')}</h2>
        <p className="text-xs text-muted mb-4">{t('settings.calLeave.hint')}</p>

        <div className="space-y-2">
          {SWITCHES.map((x) => (
            <div key={x.key} className="flex items-start gap-3 p-3 rounded bg-surface-alt">
              <span className="text-sm min-w-0 flex-1">
                <strong>{t(x.labelKey)}</strong>
                <span className="block text-xs text-muted mt-1 leading-relaxed">{t(x.hintKey)}</span>
              </span>
              <Switch
                checked={switches[x.key] ?? false}
                onChange={(next) => setSwitches({ ...switches, [x.key]: next })}
                label={t(x.labelKey)}
                disabled={readOnly}
              />
            </div>
          ))}
        </div>

        <h3 className="font-bold mt-6">{t('settings.calLeave.typesTitle')}</h3>
        <p className="text-xs text-muted mb-3">{t('settings.calLeave.typesHint')}</p>

        <ul className="space-y-3">
          {types.map((x, index) => (
            <li key={x.key ?? `new-${index}`} className="rounded border border-border p-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={t('settings.calLeave.typeIt')}>
                  <input
                    className="input"
                    maxLength={60}
                    value={x.it}
                    placeholder={t('settings.calLeave.newType')}
                    onChange={(e) => patchType(index, { it: e.target.value })}
                  />
                </Field>
                <Field label={t('settings.calLeave.typeEn')}>
                  <input
                    className="input"
                    maxLength={60}
                    value={x.en}
                    onChange={(e) => patchType(index, { en: e.target.value })}
                  />
                </Field>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={x.hours}
                    onChange={(next) => patchType(index, { hours: next })}
                    label={t('settings.calLeave.typeHours')}
                    disabled={readOnly}
                  />
                  <span>
                    <strong className="font-semibold">{t('settings.calLeave.typeHours')}</strong>
                    <span className="block text-xs text-muted">{t('settings.calLeave.typeHoursHint')}</span>
                  </span>
                </div>
                <button
                  type="button"
                  className="btn-danger ms-auto"
                  disabled={types.length <= 1}
                  title={types.length <= 1 ? t('settings.calLeave.typeKeepOne') : undefined}
                  onClick={() => setTypes(types.filter((_, i) => i !== index))}
                >
                  {t('settings.calLeave.typeRemove')}
                </button>
              </div>
            </li>
          ))}
        </ul>

        <button
          type="button"
          className="btn-secondary mt-3"
          onClick={() => setTypes([...types, { key: null, it: '', en: '', hours: false }])}
        >
          {t('settings.calLeave.typeAdd')}
        </button>
        <p className="text-xs text-muted mt-3 leading-relaxed">{t('settings.calLeave.typeRemovedNote')}</p>

        {invalid ? <p className="text-sm text-danger mt-4">{t('settings.calLeave.typeNameRequired')}</p> : null}
        {save.error ? (
          <p className="text-sm text-danger mt-4">
            {save.error instanceof ApiError ? save.error.message : t('common.saveError')}
          </p>
        ) : null}
        {saved ? <p className="text-sm text-success font-semibold mt-4">{t('settings.saved')}</p> : null}
        <button type="button" className="btn-primary mt-4" disabled={save.isPending} onClick={submit}>
          {save.isPending ? t('common.saving') : t('settings.saveSettings')}
        </button>
      </Card>
    </fieldset>
  );
}
